import { useContext, type ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { mergeSx } from "ui/merge-sx";

import CommentableModule from "containers/admin/clients/touchpoint/components/comments/commentable-module/commentable-module";

import CommentContext from "contexts/comment-context";

interface CommentableHeaderProps {
  moduleId: "senderName" | "replyToEmail" | "subjectLine" | "preheader" | "senderEmail";
  isCommentMode: boolean;
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

const HEADER_TRIGGER_SX: SxProps<Theme> = (theme) => ({ top: theme.spacing(-0.5) });

export const CommentableHeader = ({
  children,
  moduleId,
  isCommentMode,
  sx,
}: CommentableHeaderProps) => {
  const { commentData, commentingDisabled } = useContext(CommentContext);

  const comment = commentData.items.filter((comment) => comment.annotationId === moduleId)[0];

  const commentNumber = parseInt(comment?.badgeNumber ?? "0");
  const hasComment = commentNumber > 0;

  const getCommentButtonPadding = () => {
    if (!isCommentMode) {
      return "";
    }
    if (hasComment) {
      return commentNumber > 10 ? "pl-9" : "pl-7";
    }
    if (commentingDisabled) {
      return "";
    }
    return "pl-5";
  };

  const showHeaderCommentLayer = isCommentMode && (!commentingDisabled || hasComment);

  return (
    <Box
      sx={mergeSx(
        { position: "relative", cursor: showHeaderCommentLayer ? "pointer" : "default" },
        sx,
      )}>
      <span className={`flex ${getCommentButtonPadding()}`}>{children}</span>
      {showHeaderCommentLayer && (
        <CommentableModule
          moduleId={moduleId}
          comment={comment}
          commentTriggerProps={{ sx: HEADER_TRIGGER_SX }}
        />
      )}
    </Box>
  );
};
